import React, { createContext, useContext, useMemo } from "react";
import { useCallback, useEffect, useMemo as useMemoHook, useRef, useState } from "react";
import { useLocation, useOutletContext } from "react-router-dom";
import { getCurrentUser, fetchUserAttributes } from "aws-amplify/auth";
import { client } from "../../amplify-client";
import {
  acceptChatLifecycle,
  denyChatLifecycle,
  deleteChatLifecycle,
} from "../../api/communication/chat-lifecycle-service";
import {
  hideConversationForUser,
} from "../../api/communication/conversation-visibility-service";
import {
  hideMessageForUser,
  listHiddenMessageSkForUserChat,
} from "../../api/communication/message-visibility-service";
import {
  evaluateClientTextModeration,
  extractModerationFeedback,
} from "../../api/moderation/content-moderation-service";
import {
  clearCurrentOpenChatPk,
  publishCommunicationEvent,
  setCurrentOpenChatPk,
  subscribeCommunicationEvents,
} from "../../api/communication/communication-events-service";
import {
  loadCommunicationInboxSnapshot,
} from "../../api/communication/communication-inbox-service";
import { watchPresenceForUsers } from "../../api/presence";

// Derives the display label used for the current user in Active chat messaging flows.
export function getActiveSenderLabel(me) {
  return me?.name || me?.email || "You";
}

// AppSync Events API caps each channel-path segment at 50 characters, so
// the chat channel id has to be a short, deterministic value derived from
// the chatPk. Truncated SHA-256 hex (50 chars) is collision-resistant in
// practice for the chatPk space and stays inside AppSync's limit. The
// scheme is one-way — the subscribe authorizer can't reverse it back to
// the chatPk, so per-channel membership checks need a different mechanism
// (custom Lambda auth at the API level, or a stored channelId index).
async function channelIdFromChatPk(chatPk) {
  const raw = String(chatPk);
  const bytes = new TextEncoder().encode(raw);
  const hashBuffer = await crypto.subtle.digest("SHA-256", bytes);
  const hex = Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hex.slice(0, 50);
}

// Member-gated single-chat fetch. Direct Chat.list/get from clients is denied
// at the model level — only chat_members can read a Chat row, enforced by the
// chatRead lambda behind getMyChat.
async function fetchChatByPk(chatPk) {
  const resp = await client.queries.getMyChat({ chatPk });
  const errors = Array.isArray(resp?.errors) ? resp.errors : [];
  if (errors.length) {
    throw new Error(errors.map((err) => err.message).join(", "));
  }
  return resp?.data ?? null;
}

function sortAscString(a, b) {
  return String(a ?? "").localeCompare(String(b ?? ""));
}

function normalizeText(value) {
  return String(value ?? "").trim().toLowerCase();
}

function normalizeTimestampValue(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  const asDate = new Date(normalized);
  if (!Number.isNaN(asDate.getTime())) return asDate.toISOString();
  return normalized;
}

function isNewerTimestamp(candidate, previous) {
  const normalizedCandidate = normalizeTimestampValue(candidate);
  const normalizedPrevious = normalizeTimestampValue(previous);
  if (!normalizedCandidate) return false;
  if (!normalizedPrevious) return true;
  return normalizedCandidate > normalizedPrevious;
}

function sortAscBySk(a, b) {
  return String(a?.sk ?? "").localeCompare(String(b?.sk ?? ""));
}

function mergeChatsPreservingAcceptedStatus(incomingChats, existingChats) {
  const existingByChatPk = new Map(
    (Array.isArray(existingChats) ? existingChats : [])
      .map((chat) => [String(chat?.chatPk || "").trim(), chat])
      .filter(([chatPk]) => Boolean(chatPk))
  );

  return (Array.isArray(incomingChats) ? incomingChats : []).map((chat) => {
    const chatPk = String(chat?.chatPk || "").trim();
    const existingChat = existingByChatPk.get(chatPk);
    if (!existingChat) return chat;
    if (existingChat?.status === "accepted" && chat?.status === "pending") {
      return { ...chat, status: "accepted" };
    }
    return chat;
  });
}

function clearUnreadForOpenChat(chats, selectedChatPk, pathname) {
  const isActiveChatRoute = String(pathname || "").startsWith("/active/chat/");
  const normalizedSelectedChatPk = String(selectedChatPk || "").trim();
  if (!isActiveChatRoute || !normalizedSelectedChatPk || !Array.isArray(chats)) {
    return Array.isArray(chats) ? chats : [];
  }

  return chats.map((chat) =>
    chat?.chatPk === normalizedSelectedChatPk ? { ...chat, unreadCount: 0 } : chat
  );
}

function upsertChatByActivity(chats, nextChat) {
  const normalizedChatPk = String(nextChat?.chatPk || "").trim();
  if (!normalizedChatPk) {
    return Array.isArray(chats) ? chats : [];
  }

  const previousChats = Array.isArray(chats) ? chats : [];
  const remainingChats = previousChats.filter(
    (chat) => String(chat?.chatPk || "").trim() !== normalizedChatPk
  );

  return [nextChat, ...remainingChats].sort((a, b) =>
    String(b?.lastUpdatedAt || "").localeCompare(String(a?.lastUpdatedAt || ""))
  );
}

function normalizeRouteOptimisticMessage(rawMessage, selectedChatPk) {
  const normalizedSelectedChatPk = String(selectedChatPk || "").trim();
  const normalizedChatPk = String(rawMessage?.chatPk || "").trim();
  if (!normalizedSelectedChatPk || normalizedChatPk !== normalizedSelectedChatPk) {
    return null;
  }

  const text = String(rawMessage?.text || "").trim();
  const senderId = String(rawMessage?.senderId || "").trim();
  const localId =
    String(rawMessage?.localId || rawMessage?.messageId || "").trim();
  const timestamp = normalizeTimestampValue(
    rawMessage?.timestamp || rawMessage?.activityAt || rawMessage?.sentAt
  );

  if (!text || !senderId || !localId || !timestamp) {
    return null;
  }

  const messageId = String(rawMessage?.messageId || "").trim() || null;

  return {
    chatPk: normalizedChatPk,
    sender: String(rawMessage?.sender || "").trim() || senderId,
    senderId,
    text,
    timestamp,
    messageId,
    localId,
    isMe: rawMessage?.isMe !== false,
    isOptimistic: rawMessage?.isOptimistic !== false,
    deliveryStatus: String(rawMessage?.deliveryStatus || "").trim() || "sent",
  };
}

function normalizeRoutePromotedChat(rawChat, selectedChatPk) {
  const normalizedSelectedChatPk = String(selectedChatPk || "").trim();
  const normalizedChatPk = String(rawChat?.chatPk || "").trim();
  if (!normalizedSelectedChatPk || normalizedChatPk !== normalizedSelectedChatPk) {
    return null;
  }

  return {
    ...rawChat,
    chatPk: normalizedChatPk,
    status: String(rawChat?.status || "accepted").trim() || "accepted",
    unreadCount: Number(rawChat?.unreadCount || 0),
  };
}

function mergeFetchedMessagesWithOptimistic(fetchedMessages, previousMessages) {
  const nextFetchedMessages = Array.isArray(fetchedMessages) ? fetchedMessages : [];
  const priorMessages = Array.isArray(previousMessages) ? previousMessages : [];
  const fetchedMessageIds = new Set(
    nextFetchedMessages
      .map((message) => String(message?.messageId || "").trim())
      .filter(Boolean)
  );
  const fetchedLocalIds = new Set(
    nextFetchedMessages
      .map((message) => String(message?.localId || "").trim())
      .filter(Boolean)
  );

  const optimisticMessagesToKeep = priorMessages.filter((message) => {
    if (!message?.isOptimistic || message?.deliveryStatus === "failed") return false;
    const messageId = String(message?.messageId || "").trim();
    const localId = String(message?.localId || "").trim();
    if (messageId && fetchedMessageIds.has(messageId)) return false;
    if (localId && fetchedLocalIds.has(localId)) return false;
    return true;
  });

  return [...nextFetchedMessages, ...optimisticMessagesToKeep];
}

// Deduplicates chat message rows by server message id while preserving chronological order.
export function dedupeActiveMessageRowsByMessageId(rows) {
  const sorted = Array.isArray(rows) ? rows.slice().sort(sortAscBySk) : [];
  const seenMessageIds = new Set();
  const deduped = [];

  for (const row of sorted) {
    const messageId = String(row?.message_id || "").trim();
    if (messageId) {
      if (seenMessageIds.has(messageId)) continue;
      seenMessageIds.add(messageId);
    }
    deduped.push(row);
  }

  return deduped;
}

function nextLocalMessageId(counterRef) {
  counterRef.current += 1;
  return `local-${Date.now()}-${counterRef.current}`;
}

// Loads and coordinates Active screen state, realtime chat updates, and message actions.
export function useActiveData(outletContextOverride = null) {
  const location = useLocation();
  const locationState = location?.state;
  // Pick the initial chatPk from either router state (in-tab navigation)
  // or the URL query string (?chatPk=…). The query-string path lets the
  // chat screen open in a new tab — see openChatInNewTab() — and pick the
  // selected chat without relying on memory-only router state.
  const initialSelected = (() => {
    if (location?.state?.selectedChatPk) return location.state.selectedChatPk;
    if (typeof window === "undefined") return null;
    try {
      const params = new URLSearchParams(location?.search || "");
      const value = params.get("chatPk");
      return value ? value : null;
    } catch {
      return null;
    }
  })();
  const routeOutletContext = useOutletContext();
  const outletContext = outletContextOverride ?? routeOutletContext;
  const setHasActiveNotifications = outletContext?.setHasActiveNotifications;
  const communicationCache = outletContext?.communicationCache;
  const shellCurrentUser = outletContext?.currentUser;

  const [selectedChatPk, setSelectedChatPk] = useState(initialSelected);
  const [input, setInput] = useState("");
  const [me, setMe] = useState(() => ({
    userId: shellCurrentUser?.userId || null,
    email: shellCurrentUser?.email || null,
    name: shellCurrentUser?.name || null,
  }));
  const [meLoading, setMeLoading] = useState(() => !Boolean(shellCurrentUser?.userId));
  const [chatListLoading, setChatListLoading] = useState(true);
  const [availableChats, setAvailableChats] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageModerationWarning, setMessageModerationWarning] = useState("");

  const channelRef = useRef(null);
  const subRef = useRef(null);
  const optimisticMessageCounterRef = useRef(0);
  const selectedChatPkRef = useRef(selectedChatPk);
  const locationPathRef = useRef(location.pathname);
  // Ref-mirror of me.userId so the long-lived inbox-events subscription
  // can dedupe optimistic-vs-fanout reconciliation against the latest
  // value without having to be torn down each time `me` reloads.
  const mySenderIdRef = useRef(null);
  const inFlightListLoadRef = useRef(false);
  const pendingListReloadRef = useRef(false);
  const scheduledReloadRef = useRef(null);
  const latestSyncedAtRef = useRef("");
  const latestAppliedActivityByChatRef = useRef(new Map());
  const recentInboxEventIdsRef = useRef(new Set());
  const recentInboxMessageIdsRef = useRef(new Set());
  const acceptingChatRequestPromisesRef = useRef(new Map());
  const [acceptingChatRequestIds, setAcceptingChatRequestIds] = useState([]);
  const availableChatsRef = useRef([]);
  const seededRouteOptimisticIdsRef = useRef(new Set());

  useEffect(() => {
    if (initialSelected) setSelectedChatPk(initialSelected);
  }, [initialSelected]);

  useEffect(() => {
    selectedChatPkRef.current = selectedChatPk;
  }, [selectedChatPk]);

  useEffect(() => {
    availableChatsRef.current = availableChats;
  }, [availableChats]);

  useEffect(() => {
    locationPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    mySenderIdRef.current = me?.userId || null;
  }, [me?.userId]);

  const routedPendingOptimisticMessage = useMemoHook(
    () =>
      normalizeRouteOptimisticMessage(
        locationState?.pendingOptimisticMessage,
        selectedChatPk
      ),
    [locationState, selectedChatPk]
  );
  const routedPromotedChat = useMemoHook(
    () =>
      normalizeRoutePromotedChat(
        locationState?.promotedChat,
        selectedChatPk || initialSelected
      ),
    [initialSelected, locationState, selectedChatPk]
  );

  useEffect(() => {
    const isActiveChatRoute = location.pathname.startsWith("/active/chat/");
    if (!isActiveChatRoute) return;

    if (!routedPendingOptimisticMessage) return;

    const optimisticKey =
      String(
        routedPendingOptimisticMessage.localId ||
          routedPendingOptimisticMessage.messageId ||
          ""
      ).trim();
    if (!optimisticKey || seededRouteOptimisticIdsRef.current.has(optimisticKey)) return;

    seededRouteOptimisticIdsRef.current.add(optimisticKey);
    setMessages((previous) => {
      const alreadyPresent = previous.some((message) => {
        const previousMessageId = String(message?.messageId || "").trim();
        const previousLocalId = String(message?.localId || "").trim();
        return (
          (routedPendingOptimisticMessage.messageId &&
            previousMessageId === routedPendingOptimisticMessage.messageId) ||
          previousLocalId === routedPendingOptimisticMessage.localId
        );
      });
      if (alreadyPresent) return previous;
      return [...previous, routedPendingOptimisticMessage];
    });
    setMessagesLoading(false);
  }, [location.pathname, routedPendingOptimisticMessage]);

  useEffect(() => {
    const isActiveChatRoute = location.pathname.startsWith("/active/chat/");
    if (isActiveChatRoute && selectedChatPk) {
      setCurrentOpenChatPk(selectedChatPk);
    } else {
      clearCurrentOpenChatPk();
    }
    return () => {
      clearCurrentOpenChatPk();
    };
  }, [location.pathname, selectedChatPk]);

  useEffect(() => {
    let cancelled = false;
    const shellUserId = String(shellCurrentUser?.userId || "").trim();

    if (shellUserId) {
      setMe({
        userId: shellUserId,
        email: shellCurrentUser?.email || null,
        name: shellCurrentUser?.name || null,
      });
      setMeLoading(false);
      return () => {
        cancelled = true;
      };
    }

    async function loadMe() {
      try {
        const u = await getCurrentUser();
        const attrs = await fetchUserAttributes();
        if (!cancelled) {
          setMe({
            userId: u?.userId ?? null,
            email: attrs?.email || null,
            name: attrs?.name || null,
          });
        }
      } catch (e) {
        console.error("Failed to load current user:", e);
      } finally {
        if (!cancelled) setMeLoading(false);
      }
    }

    loadMe();
    return () => {
      cancelled = true;
    };
  }, [shellCurrentUser?.email, shellCurrentUser?.name, shellCurrentUser?.userId]);

  const mySenderId = me.userId;
  const mySenderLabel = useMemoHook(() => getActiveSenderLabel(me), [me]);

  useEffect(() => {
    if (!mySenderId) return;

    let cancelled = false;
    const MAX_RECENT_EVENT_IDS = 300;
    const MAX_RECENT_MESSAGE_IDS = 500;
    const FULL_REFRESH_STALE_MS = 5 * 60 * 1000;
    latestAppliedActivityByChatRef.current = new Map();
    recentInboxEventIdsRef.current = new Set();
    recentInboxMessageIdsRef.current = new Set();

    function rememberRecent(setRef, value, maxSize) {
      const normalized = String(value || "").trim();
      if (!normalized || setRef.has(normalized)) return false;
      setRef.add(normalized);
      if (setRef.size > maxSize) {
        const oldest = setRef.values().next().value;
        setRef.delete(oldest);
      }
      return true;
    }

    async function loadMyChats({ silent = false, force = false } = {}) {
      if (inFlightListLoadRef.current) {
        pendingListReloadRef.current = true;
        return;
      }

      inFlightListLoadRef.current = true;
      if (!silent) setChatListLoading(true);

      try {
        const snapshot = await loadCommunicationInboxSnapshot({
          userId: mySenderId,
          selectedChatPk: initialSelected,
          force,
          maxAgeMs: silent ? 4_000 : 0,
        });
        const withUnread = clearUnreadForOpenChat(
          Array.isArray(snapshot?.activeChats) ? snapshot.activeChats : [],
          selectedChatPkRef.current || initialSelected,
          locationPathRef.current
        );
        let mergedChats = mergeChatsPreservingAcceptedStatus(
          withUnread,
          availableChatsRef.current
        );
        const latestSyncedAt = String(snapshot?.latestSyncedAt || new Date().toISOString());
        const normalizedSelectedChatPk = String(
          selectedChatPkRef.current || initialSelected || ""
        ).trim();
        const shouldPreserveRouteSelectedChat =
          Boolean(routedPendingOptimisticMessage) &&
          Boolean(normalizedSelectedChatPk) &&
          !mergedChats.some(
            (chat) => String(chat?.chatPk || "").trim() === normalizedSelectedChatPk
          );

        if (shouldPreserveRouteSelectedChat) {
          const cachedSelectedChat =
            availableChatsRef.current.find(
              (chat) => String(chat?.chatPk || "").trim() === normalizedSelectedChatPk
            ) || null;
          const selectedChatToPreserve = cachedSelectedChat || routedPromotedChat;

          if (selectedChatToPreserve) {
            const optimisticTimestamp =
              String(routedPendingOptimisticMessage?.timestamp || "").trim() || latestSyncedAt;
            const optimisticPreview =
              String(routedPendingOptimisticMessage?.text || "").trim() ||
              String(selectedChatToPreserve?.previewText || "").trim();

            mergedChats = upsertChatByActivity(mergedChats, {
              ...selectedChatToPreserve,
              chatPk: normalizedSelectedChatPk,
              status: "accepted",
              lastUpdatedAt: optimisticTimestamp,
              previewText: optimisticPreview,
              unreadCount: 0,
            });
          }
        }

        if (!cancelled) {
          setAvailableChats(mergedChats);
          setPendingRequests([]);

          setSelectedChatPk((previous) => {
            if (!mergedChats.length) return null;
            if (previous && mergedChats.some((chat) => chat.chatPk === previous)) return previous;
            return mergedChats[0].chatPk;
          });

          communicationCache?.setSnapshot?.("active", mySenderId, {
            availableChats: mergedChats,
            pendingRequests: [],
            latestSyncedAt,
          });
        }

        latestSyncedAtRef.current = latestSyncedAt;
      } catch (e) {
        console.error("Failed to load chats:", e);
        if (!cancelled) {
          setAvailableChats([]);
          setPendingRequests([]);
          communicationCache?.setSnapshot?.("active", mySenderId, {
            availableChats: [],
            pendingRequests: [],
            latestSyncedAt: new Date().toISOString(),
          });
        }
      } finally {
        inFlightListLoadRef.current = false;

        if (pendingListReloadRef.current && !cancelled) {
          pendingListReloadRef.current = false;
          void loadMyChats({ silent: true });
        }

        if (!cancelled && !silent) setChatListLoading(false);
      }
    }

    function scheduleListReload({ immediate = false } = {}) {
      if (cancelled) return;
      if (scheduledReloadRef.current) {
        clearTimeout(scheduledReloadRef.current);
      }
      const delayMs = immediate ? 0 : 120;
      scheduledReloadRef.current = setTimeout(() => {
        scheduledReloadRef.current = null;
        void loadMyChats({ silent: true });
      }, delayMs);
    }

    async function runCatchUpSync() {
      scheduleListReload({ immediate: true });
    }

    const unsubscribeInbox = subscribeCommunicationEvents((payload) => {
      const type = String(payload?.type || "");
      if (
        type !== "chat_message" &&
        type !== "chat_request_created" &&
        type !== "chat_request_updated" &&
        type !== "chat_deleted" &&
        type !== "chat_visibility_changed"
      ) {
        return;
      }

      const eventId = String(payload?.eventId || "").trim();
      if (eventId) {
        const isNewEvent = rememberRecent(
          recentInboxEventIdsRef.current,
          eventId,
          MAX_RECENT_EVENT_IDS
        );
        if (!isNewEvent) return;
      }

      const messageId = String(payload?.messageId || "").trim();
      if (type === "chat_message" && messageId) {
        const isNewMessage = rememberRecent(
          recentInboxMessageIdsRef.current,
          messageId,
          MAX_RECENT_MESSAGE_IDS
        );
        if (!isNewMessage) return;
      }

      const chatPk = String(payload?.chatPk || "").trim();
      const activityAt = String(payload?.activityAt || payload?.sentAt || "").trim();
      if (chatPk && activityAt) {
        const latestAppliedAt = latestAppliedActivityByChatRef.current.get(chatPk) || "";
        // A message that already passed the messageId dedup is distinct by
        // definition — don't drop it solely because its timestamp ties the
        // latest applied one (same-second fanout). Only apply the strict
        // ordering guard for events without a deduped messageId.
        const passedMessageIdDedup = type === "chat_message" && Boolean(messageId);
        if (!passedMessageIdDedup && !isNewerTimestamp(activityAt, latestAppliedAt)) {
          return;
        }
        if (isNewerTimestamp(activityAt, latestAppliedAt)) {
          latestAppliedActivityByChatRef.current.set(chatPk, normalizeTimestampValue(activityAt));
        }
      }

      const isSameActiveThreadOpen =
        type === "chat_message" &&
        locationPathRef.current.startsWith("/active/chat/") &&
        chatPk &&
        chatPk === String(selectedChatPkRef.current || "");

      if (isSameActiveThreadOpen) {
        // The active chat used to subscribe to its own chat-channel for
        // live updates, but that broadcast path was an IDOR vector
        // (anyone with the chatPk could subscribe). Realtime now flows
        // exclusively through the user-channel fanout this listener is
        // already bound to, so we apply the incoming message inline
        // here using the same merge semantics the chat-channel handler
        // used (dedupe by sk/messageId, swap optimistic-by-clientId).
        const incomingMessageId =
          String(payload?.messageId || "").trim() || null;
        const ts =
          String(payload?.messageSk || payload?.sentAt || "").trim() || null;
        if (!ts && !incomingMessageId) return;

        setMessages((previous) => {
          const hasDuplicateByMessageId = incomingMessageId
            ? previous.some(
                (entry) =>
                  String(entry?.messageId || "") === incomingMessageId
              )
            : false;
          // Only fall back to timestamp-based dedup when there is no
          // messageId to use as a precise key — two distinct messages can
          // legitimately share the same second-granularity timestamp.
          const hasDuplicateByTimestamp = !incomingMessageId && ts
            ? previous.some(
                (entry) => String(entry.timestamp || "") === String(ts)
              )
            : false;
          if (hasDuplicateByTimestamp || hasDuplicateByMessageId) {
            return previous;
          }

          const senderId = String(payload?.messageFrom || "unknown");
          // Full content is now included in the fanout payload so the
          // active chat can render the message without an extra fetch
          // (the `preview` field is still there for inbox cards).
          const content = String(payload?.content ?? payload?.preview ?? "");

          const incomingMessage = {
            chatPk,
            senderId,
            sender: senderId,
            text: content,
            timestamp: ts,
            messageId: incomingMessageId,
            localId: incomingMessageId || ts,
            isMe: senderId === mySenderIdRef.current,
            isOptimistic: false,
            deliveryStatus: null,
          };

          const optimisticIdx =
            senderId === mySenderIdRef.current && incomingMessageId
              ? previous.findIndex(
                  (entry) =>
                    entry.isOptimistic &&
                    entry.deliveryStatus !== "failed" &&
                    entry.senderId === senderId &&
                    String(entry?.messageId || "") === incomingMessageId
                )
              : -1;

          if (optimisticIdx >= 0) {
            const updated = previous.slice();
            updated[optimisticIdx] = {
              ...updated[optimisticIdx],
              ...incomingMessage,
              deliveryStatus: "delivered",
            };
            return updated;
          }

          return [...previous, incomingMessage];
        });
        return;
      }
      scheduleListReload();
    });

    const handleFocus = () => {
      if (document.visibilityState === "hidden") return;
      void runCatchUpSync();
    };
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      void runCatchUpSync();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    const cachedSnapshot = communicationCache?.getSnapshot?.("active", mySenderId);
    const cachedPayload = cachedSnapshot?.payload;
    const cachedChats = Array.isArray(cachedPayload?.availableChats)
      ? cachedPayload.availableChats
      : [];
    const cachedPendingRequests = Array.isArray(cachedPayload?.pendingRequests)
      ? cachedPayload.pendingRequests
      : [];
    const hasCachedData = cachedChats.length > 0 || cachedPendingRequests.length > 0;
    // The cache only includes chats that pass the visibility filter, so a
    // hidden chat is absent. When the user opens a hidden conversation
    // (e.g. through Browse and Connect), short-circuiting on the cache
    // would hijack the URL pk to the wrong chat. Force a fresh fetch
    // instead — loadMyChats passes selectedChatPk so the snapshot
    // synthesizes the hidden chat as a one-off candidate.
    const cacheCoversInitialSelected =
      !initialSelected || cachedChats.some((chat) => chat.chatPk === initialSelected);
    const cacheUsable = hasCachedData && cacheCoversInitialSelected;

    if (cacheUsable && !cancelled) {
      availableChatsRef.current = cachedChats;
      setAvailableChats(cachedChats);
      setPendingRequests(cachedPendingRequests);
      setChatListLoading(false);
      setSelectedChatPk((previous) => {
        if (previous && cachedChats.some((chat) => chat.chatPk === previous)) return previous;
        if (initialSelected && cachedChats.some((chat) => chat.chatPk === initialSelected)) {
          return initialSelected;
        }
        return cachedChats[0]?.chatPk || null;
      });
      latestSyncedAtRef.current = String(
        cachedPayload?.latestSyncedAt || cachedSnapshot?.updatedAt || ""
      );
    } else {
      void loadMyChats();
    }

    const cacheUpdatedAtMs = new Date(cachedSnapshot?.updatedAt || "").getTime();
    const isCacheStale =
      !cacheUsable ||
      Number.isNaN(cacheUpdatedAtMs) ||
      Date.now() - cacheUpdatedAtMs > FULL_REFRESH_STALE_MS;

    if (cacheUsable && isCacheStale) {
      void loadMyChats({ silent: true });
    }

    if (cacheUsable) {
      void runCatchUpSync();
    }

    return () => {
      cancelled = true;
      unsubscribeInbox();
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (scheduledReloadRef.current) {
        clearTimeout(scheduledReloadRef.current);
        scheduledReloadRef.current = null;
      }
      pendingListReloadRef.current = false;
      inFlightListLoadRef.current = false;
    };
  }, [
    communicationCache,
    initialSelected,
    mySenderId,
    routedPendingOptimisticMessage,
    routedPromotedChat,
  ]);

  useEffect(() => {
    if (!mySenderId) return;
    if (chatListLoading && !availableChats.length && !pendingRequests.length) return;
    communicationCache?.setSnapshot?.("active", mySenderId, {
      availableChats,
      pendingRequests,
      latestSyncedAt: latestSyncedAtRef.current || new Date().toISOString(),
    });
  }, [
    availableChats,
    chatListLoading,
    communicationCache,
    mySenderId,
    pendingRequests,
  ]);

  // Live presence: watch the loaded 1:1 counterparts over AppSync Events and
  // patch presenceStatus in place, so the list dots and the open chat header
  // (currentChat derives from this array) flip without waiting for an inbox
  // rebuild. Initial values come from listPresenceByUserIds during the inbox
  // build; the watcher only delivers transitions. Keyed on the joined id
  // list so presence patches (which replace the chats array) don't tear down
  // and recreate subscriptions.
  const presenceWatchKey = useMemoHook(() => {
    const ids = new Set();
    availableChats.forEach((chat) => {
      if (chat?.isGroup) return;
      const counterpartId = String(chat?.counterpartId || "").trim();
      if (counterpartId) ids.add(counterpartId);
    });
    return [...ids].sort().join("|");
  }, [availableChats]);

  useEffect(() => {
    if (!presenceWatchKey) return undefined;
    const counterpartIds = presenceWatchKey.split("|");
    return watchPresenceForUsers(counterpartIds, (userId, presence) => {
      setAvailableChats((previousChats) => {
        let changed = false;
        const nextChats = previousChats.map((chat) => {
          if (String(chat?.counterpartId || "").trim() !== userId) return chat;
          const presenceStatus = presence.online ? "online" : "offline";
          const lastSeenAt = presence.lastSeenAt || chat.lastSeenAt || null;
          if (chat.presenceStatus === presenceStatus && chat.lastSeenAt === lastSeenAt) {
            return chat;
          }
          changed = true;
          return { ...chat, presenceStatus, lastSeenAt };
        });
        return changed ? nextChats : previousChats;
      });
    });
  }, [presenceWatchKey]);

  useEffect(() => {
    const isActiveChatRoute = location.pathname.startsWith("/active/chat/");
    if (!isActiveChatRoute || !selectedChatPk) return;
    if (!mySenderId) {
      if (!routedPendingOptimisticMessage) setMessagesLoading(true);
      return;
    }

    let cancelled = false;

    async function loadMessages() {
      if (!routedPendingOptimisticMessage) {
        setMessagesLoading(true);
      }
      try {
        let nextToken = undefined;
        const rows = [];

        // Member-gated read: the listMyChatMessages custom query verifies
        // the caller is in Chat.chat_members before returning rows. Direct
        // ChatMessage.list reads from clients are denied at the model level.
        do {
          const resp = await client.queries.listMyChatMessages({
            chatPk: selectedChatPk,
            limit: 200,
            nextToken,
          });

          const errors = Array.isArray(resp?.errors) ? resp.errors : [];
          if (errors.length) throw new Error(errors.map((e) => e.message).join(", "));

          const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
          rows.push(...items);
          nextToken = resp?.data?.nextToken ?? null;
        } while (nextToken);
        const dedupedRows = dedupeActiveMessageRowsByMessageId(rows);

        const hiddenMessageSk = await listHiddenMessageSkForUserChat({
          userId: mySenderId,
          chatPk: selectedChatPk,
        });

        const mapped = dedupedRows
          .slice()
          .sort((a, b) => sortAscString(a.sk, b.sk))
          .filter((m) => !hiddenMessageSk.has(String(m.sk || "")))
          .map((m) => ({
            chatPk: selectedChatPk,
            sender: m.message_from,
            senderId: m.message_from,
            text: m.content,
            timestamp: m.sk,
            messageId: m.message_id || null,
            isMe: m.message_from === mySenderId,
            localId: m.sk,
            isOptimistic: false,
            deliveryStatus: null,
          }));

        if (!cancelled) {
          setMessages((previous) => mergeFetchedMessagesWithOptimistic(mapped, previous));
        }
      } catch (e) {
        console.error("Failed to load messages:", e);
        if (!cancelled) {
          setMessages((previous) =>
            routedPendingOptimisticMessage
              ? mergeFetchedMessagesWithOptimistic([], previous)
              : []
          );
        }
      } finally {
        if (!cancelled) setMessagesLoading(false);
      }
    }

    loadMessages();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, mySenderId, routedPendingOptimisticMessage, selectedChatPk]);

  useEffect(() => {
    if (typeof setHasActiveNotifications !== "function") return;
    const hasUnread = availableChats.some((chat) => {
      const isPendingIncoming = chat?.status === "pending" && !chat?.isInitiatedByMe;
      return isPendingIncoming || Number(chat?.unreadCount || 0) > 0;
    });
    setHasActiveNotifications(hasUnread);
  }, [availableChats, setHasActiveNotifications]);

  useEffect(() => {
    const isActiveChatRoute = location.pathname.startsWith("/active/chat/");
    if (!isActiveChatRoute || !selectedChatPk) return;

    setAvailableChats((previous) =>
      previous.map((chat) =>
        chat.chatPk === selectedChatPk ? { ...chat, unreadCount: 0 } : chat
      )
    );
  }, [location.pathname, selectedChatPk]);

  useEffect(() => {
    const isActiveChatRoute = location.pathname.startsWith("/active/chat/");
    if (!isActiveChatRoute || !selectedChatPk || !mySenderId) return;

    let cancelled = false;

    // Open the chat channel as a publish ingress only — sendMessage uses
    // channelRef.current to call channel.publish(...). We deliberately do
    // NOT call channel.subscribe here: realtime delivery goes through the
    // user-channel fanout subscription owned by App.function.js, which
    // forwards onto the local inbox-events bus that this hook already
    // listens to. Subscribing here would re-open the IDOR where any
    // signed-in user could subscribe to a chat by reconstructing its
    // channel id from the publicly-discoverable cognitoIds of the
    // participants.
    (async () => {
      const { events } = await import("aws-amplify/data");

      try {
        channelRef.current?.close?.();
      } catch {}
      subRef.current = null;
      channelRef.current = null;

      const channelId = await channelIdFromChatPk(selectedChatPk);
      const channelPath = `default/chat/${channelId}`;

      try {
        const channel = await events.connect(channelPath);

        if (cancelled) {
          try {
            channel?.close?.();
          } catch {}
          return;
        }

        channelRef.current = channel;
      } catch (e) {
        console.error("events connect failed", e);
      }
    })();

    return () => {
      cancelled = true;
      try {
        channelRef.current?.close?.();
      } catch {}
      subRef.current = null;
      channelRef.current = null;
    };
  }, [location.pathname, selectedChatPk, mySenderId]);

  const sendMessage = useCallback(async (textOverride = null) => {
    const text = String(textOverride ?? input).trim();
    if (!text || !selectedChatPk) return;
    const localModeration = evaluateClientTextModeration({
      fieldType: "message",
      text,
    });
    if (localModeration.blocked) {
      setMessageModerationWarning(localModeration.feedback);
      return;
    }
    setMessageModerationWarning("");

    const localId = nextLocalMessageId(optimisticMessageCounterRef);
    const clientMessageId = localId;
    const optimisticMessageId = `MESSAGE#CLIENT#${clientMessageId}`;
    const optimisticMessage = {
      chatPk: selectedChatPk,
      sender: mySenderLabel || "You",
      senderId: mySenderId || "local-user",
      text,
      timestamp: new Date().toISOString(),
      messageId: optimisticMessageId,
      localId,
      isMe: true,
      isOptimistic: true,
      deliveryStatus: "sent",
    };

    const failOptimisticMessage = () => {
      setMessages((previous) =>
        previous.map((messageEntry) =>
          messageEntry.localId === localId
            ? { ...messageEntry, isOptimistic: false, deliveryStatus: "failed" }
            : messageEntry
        )
      );
    };

    setMessages((previous) => [...previous, optimisticMessage]);
    if (textOverride === null || textOverride === undefined) {
      setInput("");
    }

    const selectedChat = availableChats.find((chat) => chat.chatPk === selectedChatPk) || null;
    const acceptedPeerChat = selectedChat
      ? availableChats.find(
          (chat) =>
            chat.status === "accepted" &&
            chat.chatPk !== selectedChatPk &&
            normalizeText(chat.title) === normalizeText(selectedChat.title)
        ) || null
      : null;

    try {
      const chat = await fetchChatByPk(selectedChatPk);
      if (!chat) {
        try {
          await deleteChatLifecycle(selectedChatPk);
        } catch (cleanupError) {
          console.warn("Stale chat cleanup failed:", cleanupError);
        }
        setAvailableChats((previous) => previous.filter((entry) => entry.chatPk !== selectedChatPk));
        setPendingRequests((previous) => previous.filter((entry) => entry.chatPk !== selectedChatPk));

        const fallbackAcceptedChat = availableChats.find((entry) => entry.status === "accepted");
        const fallbackAnyChat = availableChats[0];
        const fallbackChat = fallbackAcceptedChat || fallbackAnyChat || null;
        if (acceptedPeerChat) {
          setSelectedChatPk(acceptedPeerChat.chatPk);
          console.warn(
            `Selected chat no longer exists (${selectedChatPk}). Switched to accepted chat ${acceptedPeerChat.chatPk}. Send again.`
          );
          setMessageModerationWarning("That conversation is gone. We switched you to another — tap send again.");
          failOptimisticMessage();
          return;
        }
        if (fallbackChat && fallbackChat.chatPk !== selectedChatPk) {
          setSelectedChatPk(fallbackChat.chatPk);
          console.warn(
            `Selected chat no longer exists (${selectedChatPk}). Switched to ${fallbackChat.chatPk}. Send again.`
          );
          setMessageModerationWarning("That conversation is gone. We switched you to another — tap send again.");
          failOptimisticMessage();
          return;
        }
        console.warn(`Selected chat not found (${selectedChatPk}).`);
        setMessageModerationWarning("This conversation could not be found. Please select a chat and try again.");
        failOptimisticMessage();
        return;
      }
      if (chat.status !== "accepted") {
        if (acceptedPeerChat) {
          setSelectedChatPk(acceptedPeerChat.chatPk);
          console.warn(
            `Selected chat is ${chat.status} (${selectedChatPk}). Switched to accepted chat ${acceptedPeerChat.chatPk}. Send again.`
          );
          setMessageModerationWarning("This chat hasn't been accepted yet. We switched you to an active one — tap send again.");
          failOptimisticMessage();
          return;
        }
        console.warn(
          `Chat is not in accepted state yet (chatPk=${selectedChatPk}, status=${chat.status}). Message was not sent.`
        );
        setMessageModerationWarning("This chat hasn't been accepted yet. Your message wasn't sent.");
        failOptimisticMessage();
        return;
      }
      const members = Array.isArray(chat.chat_members) ? chat.chat_members : [];
      if (!mySenderId || !members.includes(mySenderId)) {
        console.warn("You are no longer authorized for this chat.");
        setMessageModerationWarning("You are no longer a member of this chat. Your message wasn't sent.");
        failOptimisticMessage();
        return;
      }
    } catch (error) {
      console.error("sendMessage preflight failed:", error);
      setMessageModerationWarning("Something went wrong verifying this chat. Please try again.");
      failOptimisticMessage();
      return;
    }

    if (!selectedChat) {
      console.warn("Chat state is still loading. Please try again.");
      setMessageModerationWarning("Chat is still loading. Please wait a moment and try again.");
      failOptimisticMessage();
      return;
    }
    if (selectedChat.status !== "accepted") {
      console.warn("You can only send messages after the chat request is accepted.");
      setMessageModerationWarning("You can only send messages after the chat request is accepted.");
      failOptimisticMessage();
      return;
    }

    const channel = channelRef.current;
    if (!channel) {
      console.warn("No active chat channel yet (still connecting). Try again.");
      setMessageModerationWarning("Still connecting to the chat. Please try again in a moment.");
      failOptimisticMessage();
      return;
    }

    try {
      await channel.publish({
        chatPk: selectedChatPk,
        content: text,
        clientMessageId,
      });
      // Transport ACK maps to "sent" — the message reached the server.
      // We only promote to "delivered" when the fanout echo for this
      // clientMessageId arrives (handled in the setMessages reconcile block
      // ~671-689). If the fanout never arrives (silent moderation drop, etc.)
      // the bubble stays "Sent" instead of showing a misleading "Delivered".
      setMessageModerationWarning("");
    } catch (e) {
      console.error("events publish failed:", e);
      const moderationFeedback = extractModerationFeedback(e);
      const publishErrorMessages = [
        e?.message,
        ...(Array.isArray(e?.errors) ? e.errors.map((entry) => entry?.message) : []),
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .join(" | ");
      const isModerationUnavailable = /CONTENT_MODERATION_UNAVAILABLE/i.test(
        publishErrorMessages
      );
      const isInternalPublishError = /INTERNAL_PUBLISH_ERROR/i.test(
        publishErrorMessages
      );
      const isDependencyFailed = /DependencyFailedException/i.test(
        publishErrorMessages
      );

      if (isModerationUnavailable || isInternalPublishError) {
        setMessageModerationWarning(
          "We are unable to send a message due to an internal error. Please come back again later."
        );
      } else if (moderationFeedback.isModerationError) {
        setMessageModerationWarning(moderationFeedback.feedback);
      } else if (isDependencyFailed) {
        setMessageModerationWarning(
          "This message may violate our safety policy and could not be sent. Please edit and try again."
        );
      } else {
        setMessageModerationWarning(
          "We are unable to send a message due to an internal error. Please come back again later."
        );
      }
      const publishErrors = Array.isArray(e?.errors) ? e.errors : [];
      if (publishErrors.length) {
        console.error(
          "events publish error details:",
          publishErrors.map((err) => err?.message ?? String(err)).join(" | ")
        );
      }
      failOptimisticMessage();
    }
  }, [
    availableChats,
    input,
    mySenderId,
    mySenderLabel,
    selectedChatPk,
  ]);

  const acceptChatRequest = async (chatId) => {
    const normalizedChatId = String(chatId || "").trim();
    if (!normalizedChatId) return false;

    const existingPromise = acceptingChatRequestPromisesRef.current.get(normalizedChatId);
    if (existingPromise) {
      await existingPromise;
      return true;
    }

    const previousAvailableChats = availableChats;
    const previousPendingRequests = pendingRequests;
    const acceptPromise = (async () => {
      setAcceptingChatRequestIds((previous) =>
        previous.includes(normalizedChatId) ? previous : [...previous, normalizedChatId]
      );

      await acceptChatLifecycle(normalizedChatId);
      setPendingRequests((previous) =>
        previous.filter((chat) => chat.chatPk !== normalizedChatId)
      );
      setAvailableChats((previous) =>
        previous.map((chat) =>
          chat.chatPk === normalizedChatId ? { ...chat, status: "accepted" } : chat
        )
      );
      publishCommunicationEvent({
        type: "chat_request_updated",
        chatPk: normalizedChatId,
        status: "accepted",
        activityAt: new Date().toISOString(),
      });
      return true;
    })();

    acceptingChatRequestPromisesRef.current.set(normalizedChatId, acceptPromise);

    try {
      return await acceptPromise;
    } catch (error) {
      setPendingRequests(previousPendingRequests);
      setAvailableChats(previousAvailableChats);
      console.error("Error accepting chat request:", error);
      throw error;
    } finally {
      acceptingChatRequestPromisesRef.current.delete(normalizedChatId);
      setAcceptingChatRequestIds((previous) =>
        previous.filter((id) => id !== normalizedChatId)
      );
    }
  };

  const denyChatRequest = async (chatId) => {
    const previousAvailableChats = availableChats;
    const previousPendingRequests = pendingRequests;
    try {
      setPendingRequests((previous) => previous.filter((chat) => chat.chatPk !== chatId));
      setAvailableChats((previous) => previous.filter((chat) => chat.chatPk !== chatId));
      if (selectedChatPk === chatId) {
        setSelectedChatPk(null);
      }

      await denyChatLifecycle(chatId);
      publishCommunicationEvent({
        type: "chat_request_updated",
        chatPk: chatId,
        status: "denied",
        activityAt: new Date().toISOString(),
      });
    } catch (error) {
      setPendingRequests(previousPendingRequests);
      setAvailableChats(previousAvailableChats);
      console.error("Error denying chat request:", error);
    }
  };

  const deleteChatRequest = async (chatId) => {
    const previousAvailableChats = availableChats;
    const previousPendingRequests = pendingRequests;
    const previousSelectedChatPk = selectedChatPk;
    try {
      setPendingRequests((previous) => previous.filter((chat) => chat.chatPk !== chatId));
      setAvailableChats((previous) => previous.filter((chat) => chat.chatPk !== chatId));
      if (selectedChatPk === chatId) {
        setSelectedChatPk(null);
      }

      await deleteChatLifecycle(chatId);
      publishCommunicationEvent({
        type: "chat_deleted",
        chatPk: chatId,
        activityAt: new Date().toISOString(),
      });
    } catch (error) {
      setPendingRequests(previousPendingRequests);
      setAvailableChats(previousAvailableChats);
      setSelectedChatPk(previousSelectedChatPk);
      console.error("Error deleting chat:", error);
    }
  };

  const hideConversation = async (chatId) => {
    if (!mySenderId || !chatId) return;
    const previousAvailableChats = availableChats;
    const previousPendingRequests = pendingRequests;
    const previousSelectedChatPk = selectedChatPk;
    try {
      setPendingRequests((previous) => previous.filter((chat) => chat.chatPk !== chatId));
      setAvailableChats((previous) => previous.filter((chat) => chat.chatPk !== chatId));
      if (selectedChatPk === chatId) {
        setSelectedChatPk(null);
        setMessages([]);
      }

      await hideConversationForUser({ userId: mySenderId, chatPk: chatId });
      publishCommunicationEvent({
        type: "chat_visibility_changed",
        chatPk: chatId,
        status: "hidden",
        activityAt: new Date().toISOString(),
      });
    } catch (error) {
      setPendingRequests(previousPendingRequests);
      setAvailableChats(previousAvailableChats);
      setSelectedChatPk(previousSelectedChatPk);
      console.error("Error hiding conversation:", error);
    }
  };

  const hideMessage = async ({ chatPk, messageTimestamp }) => {
    if (!mySenderId || !chatPk || !messageTimestamp) return false;

    const normalizedMessageSk = String(messageTimestamp);
    try {
      await hideMessageForUser({
        userId: mySenderId,
        chatPk,
        messageSk: normalizedMessageSk,
      });

      if (selectedChatPk === chatPk) {
        setMessages((previous) =>
          previous.filter((messageEntry) => String(messageEntry?.timestamp || "") !== normalizedMessageSk)
        );
      }
      return true;
    } catch (error) {
      console.error("Error hiding message:", error);
      return false;
    }
  };

  const visibleMessages = useMemoHook(() => {
    const normalizedSelectedChatPk = String(selectedChatPk || "").trim();
    if (!normalizedSelectedChatPk) return [];
    return messages.filter((message) => {
      const messageChatPk = String(message?.chatPk || "").trim();
      // Defense in depth: drop any message that is stamped with a different chatPk.
      // Legacy entries without chatPk fall through (older code paths).
      return !messageChatPk || messageChatPk === normalizedSelectedChatPk;
    });
  }, [messages, selectedChatPk]);

  const enrichedChats = useMemoHook(() => {
    return availableChats.map((chat) => {
      if (chat.chatPk !== selectedChatPk) return chat;
      const last = visibleMessages?.[visibleMessages.length - 1];
      return {
        ...chat,
        previewText: last?.text || chat.previewText || "",
      };
    });
  }, [availableChats, selectedChatPk, visibleMessages]);

  const currentChat = useMemoHook(
    () => enrichedChats.find((chat) => chat.chatPk === selectedChatPk) || null,
    [enrichedChats, selectedChatPk]
  );

  return {
    meLoading,
    me: { ...me, label: mySenderLabel },
    chatListLoading,
    availableChats: enrichedChats,
    pendingRequests,
    acceptingChatRequestIds,
    selectedChatPk,
    setSelectedChatPk,
    messagesLoading,
    messages: visibleMessages,
    input,
    setInput,
    sendMessage,
    messageModerationWarning,
    clearMessageModerationWarning: () => setMessageModerationWarning(""),
    acceptChatRequest,
    denyChatRequest,
    deleteChatRequest,
    hideConversation,
    hideMessage,
    currentChat,
  };
}

const ActiveContext = createContext(null);

// Provides the shared Active controller state to Active screen routes and chat children.
export function ActiveProvider({ children, outletContextOverride = null }) {
  const controller = useActiveData(outletContextOverride);
  const value = useMemo(() => controller, [controller]);
  return <ActiveContext.Provider value={value}>{children}</ActiveContext.Provider>;
}

// Reads the current Active controller value and enforces provider usage.
export function useActive() {
  const ctx = useContext(ActiveContext);
  if (!ctx) throw new Error("useActive must be used inside <ActiveProvider>");
  return ctx;
}
